/**
 * *Spis k dátumu* of the open case (PRD-0075 R-D4): the day the dashboard, entity histories and
 * hypotheses read as of. The provider sits in the case layout: it reads the day from the URL and
 * keeps it with the case while the reader moves through its pages — a link inside the case that
 * drops `?asOf=` gets the day back, without a history entry; clearing the day forgets it. Graph,
 * timeline and map ignore it. Outside a case (and in stories without a provider) it is now.
 */
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { asOfDay, asOfInstant } from '@/components/case-as-of/as-of-day'
import { AS_OF_PARAM, keepAsOfDay, keptAsOfDay, withAsOfDay } from './case-as-of'

export interface CaseAsOfState {
  /** `YYYY-MM-DD`, or null for the file as it is now. */
  day: string | null
  setDay: (day: string | null) => void
}

/** Exported for stories, which set a day without a router. */
export const CaseAsOfContext = createContext<CaseAsOfState>({ day: null, setDay: () => {} })

export function CaseAsOfProvider({ caseId, children }: { caseId: string; children: ReactNode }) {
  const [params, setParams] = useSearchParams()
  const day = asOfDay(params.get(AS_OF_PARAM))

  useEffect(() => {
    if (day) {
      keepAsOfDay(caseId, day)
      return
    }
    const kept = keptAsOfDay(caseId)
    if (kept) setParams(current => withAsOfDay(current, kept), { replace: true })
  }, [caseId, day, setParams])

  const setDay = useCallback(
    (next: string | null) => {
      keepAsOfDay(caseId, next)
      setParams(current => withAsOfDay(current, next))
    },
    [caseId, setParams]
  )
  const value = useMemo(() => ({ day, setDay }), [day, setDay])
  return <CaseAsOfContext.Provider value={value}>{children}</CaseAsOfContext.Provider>
}

export interface CaseAsOf extends CaseAsOfState {
  /** The instant the reads are as of (the end of `day`), undefined for now: what the API clients take. */
  asOf: string | undefined
}

export function useCaseAsOf(): CaseAsOf {
  const { day, setDay } = useContext(CaseAsOfContext)
  return { day, setDay, asOf: asOfInstant(day) ?? undefined }
}
